<template>
  <div
    ref="card"
    class="card h-100 ai-chat-card"
    @dragenter="onDragEnter"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <div class="card-body d-flex flex-column">
      <h4 class="fs-8 font-serif mb-3">
        {{ title ?? $t("ai_chat.title") }}
      </h4>

      <div ref="threadRef" class="chat-thread flex-grow-1 mb-3">
        <div
          v-for="(msg, index) in messages"
          :key="index"
          class="chat-message d-flex mb-2"
          :class="
            msg.role === 'user'
              ? 'justify-content-end'
              : 'justify-content-start'
          "
        >
          <div
            class="chat-bubble fs-9 px-3 py-2"
            :class="
              msg.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-assistant'
            "
          >
            <div
              v-for="fileName in msg.fileNames ?? []"
              :key="fileName"
              class="chat-attachment fw-medium mb-1"
            >
              <FontAwesomeIcon icon="paperclip" class="me-1" />
              {{ fileName }}
            </div>
            <span class="chat-text">{{ msg.text }}</span>
          </div>
        </div>
        <div v-if="isSending" class="chat-message d-flex mb-2">
          <div class="chat-bubble chat-bubble-assistant fs-9 px-3 py-2">
            <div>
              <span
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              <span class="text-body-secondary">{{
                $t("ai_chat.thinking")
              }}</span>
            </div>
            <div v-if="pendingHint" class="text-body-secondary mt-1">
              {{ pendingHint }}
            </div>
          </div>
        </div>
      </div>

      <div
        v-for="(file, index) in pendingFiles"
        :key="`${file.name}-${index}`"
        class="pending-file fs-9 mb-2"
      >
        <FontAwesomeIcon icon="paperclip" class="me-1" />
        {{ file.name }}
        <button
          type="button"
          class="btn btn-link btn-sm p-0 ms-2 align-baseline"
          @click="pendingFiles.splice(index, 1)"
        >
          {{ $t("common.remove") }}
        </button>
      </div>

      <form class="d-flex gap-2 align-items-end" @submit.prevent="send">
        <input
          class="visually-hidden"
          type="file"
          ref="fileInput"
          :accept="ACCEPTED_FILE_EXTENSIONS.join(',')"
          multiple
          @change="onFilesChosen"
        />
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm flex-shrink-0"
          :title="$t('ai_chat.attach')"
          :aria-label="$t('ai_chat.attach')"
          :disabled="isSending"
          @click="fileInput?.click()"
        >
          <FontAwesomeIcon icon="paperclip" />
        </button>
        <textarea
          v-model="input"
          class="form-control form-control-sm chat-input"
          rows="1"
          :placeholder="$t('ai_chat.input_placeholder')"
          :disabled="isSending"
          @keydown.enter.exact.prevent="send"
        ></textarea>
        <button
          type="submit"
          class="btn btn-primary btn-sm flex-shrink-0"
          :disabled="isSending || (!input.trim() && pendingFiles.length === 0)"
        >
          <FontAwesomeIcon icon="paper-plane" class="me-1" />
          {{ $t("ai_chat.send") }}
        </button>
      </form>

      <div class="mt-2 text-end">
        <button
          v-if="conversationId !== null"
          type="button"
          class="btn btn-link btn-sm p-0"
          :disabled="isSending"
          @click="resetChat"
        >
          <FontAwesomeIcon icon="sync" class="me-1" />
          {{ $t("ai_chat.new_chat") }}
        </button>
      </div>
    </div>

    <div
      v-if="isDraggingFiles"
      class="drop-overlay position-absolute d-flex flex-column align-items-center justify-content-center gap-2 text-center px-3"
      :class="{ 'drop-overlay-busy': isSending }"
    >
      <template v-if="isSending">
        <FontAwesomeIcon icon="clock" class="drop-overlay-icon fs-7" />
        <div class="fs-9 fw-semibold text-body-secondary">
          {{ $t("ai_chat.drop_busy") }}
        </div>
      </template>
      <template v-else>
        <FontAwesomeIcon
          icon="cloud-upload-alt"
          class="drop-overlay-icon fs-7"
        />
        <h4 class="drop-overlay-title fs-8 font-serif mb-0">
          {{ $t("ai_chat.drop_files") }}
        </h4>
        <div class="fs-9 text-body-secondary">
          {{ $t("ai_chat.drop_files_hint", { maxMb: MAX_FILE_SIZE_MB }) }}
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { carriesFiles } from "@/composables/useStrayFileDropGuard";
import type {
  ChatTurnCorrection,
  ChatTurnResponse,
} from "@/dashboard/inventory_management/common/aiChatTypes";
import { useEventListener } from "@vueuse/core";
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

const ACCEPTED_FILE_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".csv",
  ".txt",
];
const MAX_FILE_SIZE_MB = 25;

interface ChatMessage {
  role: "user" | "assistant";
  text: string;
  fileNames?: string[];
}

const props = defineProps<{
  companyId: string | number;
  /** Chat endpoint for this page, e.g. `/api/company/1/portfolio/cancel-chat`. */
  endpoint: string;
  /** Card heading; defaults to the generic $t("ai_chat.title"). */
  title?: string;
  /** Page-specific opening message, e.g. $t("ai_chat.greetings.cancel"). */
  greeting: string;
  /** Optional extra line under the "Working on it…" spinner, e.g. a duration expectation. */
  pendingHint?: string;
  /** Page-owned wording for a rejected turn's correction; null falls back to the generic reply. */
  resolveCorrection?: (correction: ChatTurnCorrection) => string | null;
  /** Extra multipart fields sent with every turn, e.g. a page context the backend prompt embeds. */
  extraFields?: Record<string, string>;
  /**
   * A page-owned reply for a turn the backend cannot act on, e.g. a message without the document the page needs.
   * A string is shown as the assistant's reply and the turn is not sent; null sends the turn as usual.
   */
  answerLocally?: (turn: { message: string; files: File[] }) => string | null;
}>();

const emit = defineEmits<{
  /**
   * Fired with the page's result payload (same shape as the page's legacy analyze endpoint)
   * whenever a turn produces one, and with null when the chat is reset.
   */
  "analysis-complete": [data: unknown | null];
  /** Mirrors the in-flight state so pages can e.g. disable their submit while a turn runs. */
  pending: [value: boolean];
  /** The backend conversation id, once known — lets pages persist provenance on commit. */
  conversation: [id: number];
}>();

const { t, locale } = useI18n();
const { globalErrorHandler } = useGlobalHandlers();

const messages = ref<ChatMessage[]>([]);
const conversationId = ref<number | null>(null);
const input = ref("");
const pendingFiles = ref<File[]>([]);
const isSending = ref(false);
watch(isSending, (value) => emit("pending", value));
// Bumped by resetChat so a turn that was in flight when the chat was abandoned cannot revive the
// old conversation or publish its result when the response finally lands.
let chatEpoch = 0;
watch(conversationId, (value) => {
  if (value !== null) emit("conversation", value);
});
const fileInput = useTemplateRef<HTMLInputElement>("fileInput");
const threadRef = useTemplateRef<HTMLElement>("threadRef");
const card = useTemplateRef<HTMLElement>("card");
// dragenter/dragleave fire for every child the pointer crosses, so the overlay follows their balance
// rather than the latest event, which would flicker it while a file moves over the bubbles. Not
// VueUse's useDropZone: on Safari it skips its validity check, so text dragged into the textarea
// would raise the overlay and be swallowed on drop.
const dragDepth = ref(0);
const isDraggingFiles = computed(() => dragDepth.value > 0);

const greet = () => {
  messages.value = [{ role: "assistant", text: props.greeting }];
};

onMounted(greet);

const scrollToBottom = async () => {
  await nextTick();
  threadRef.value?.scrollTo({ top: threadRef.value.scrollHeight });
};

const validateFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  if (!ACCEPTED_FILE_EXTENSIONS.some((extension) => name.endsWith(extension))) {
    globalErrorHandler(t("errors.unsupportedFileType"));
    return false;
  }
  if (file.size > MAX_FILE_SIZE_MB * 1_048_576) {
    globalErrorHandler(t("errors.fileTooLarge"));
    return false;
  }
  return true;
};

const attachFiles = (files: Iterable<File>) => {
  for (const file of files) {
    if (validateFile(file)) {
      pendingFiles.value.push(file);
    }
  }
};

const onFilesChosen = (event: Event) => {
  attachFiles((event.target as HTMLInputElement).files ?? []);
  if (fileInput.value) {
    fileInput.value.value = "";
  }
};

const onDragEnter = (event: DragEvent) => {
  if (!carriesFiles(event)) return;
  event.preventDefault();
  dragDepth.value += 1;
};

const onDragOver = (event: DragEvent) => {
  if (!carriesFiles(event)) return;
  event.preventDefault();
  // Files can't join a turn that is already on its way, the same as the disabled attach button.
  event.dataTransfer.dropEffect = isSending.value ? "none" : "copy";
};

const onDragLeave = (event: DragEvent) => {
  if (!carriesFiles(event)) return;
  dragDepth.value = Math.max(0, dragDepth.value - 1);
};

const onDrop = (event: DragEvent) => {
  if (!carriesFiles(event)) return;
  event.preventDefault();
  dragDepth.value = 0;
  if (!isSending.value) {
    attachFiles(event.dataTransfer.files);
  }
};

// A bubble removed from under the pointer (the "Working on it…" one, as the reply lands) takes its
// dragleave with it, so the balance stays above zero after the file leaves. A dragover anywhere
// outside the card proves the file isn't over it any more.
useEventListener(window, "dragover", ({ target }: DragEvent) => {
  if (!(target instanceof Node && card.value?.contains(target))) {
    dragDepth.value = 0;
  }
});

/** Wording for corrections any chat page can receive; a page's own resolver goes first. */
const genericCorrection = ({
  error_type,
}: ChatTurnCorrection): string | null =>
  error_type === "DOCUMENT_IRRELEVANT"
    ? t("ai_chat.document_irrelevant")
    : null;

const send = async () => {
  const message = input.value.trim();
  const files = pendingFiles.value;
  if (isSending.value || (!message && files.length === 0)) {
    return;
  }

  messages.value.push({
    role: "user",
    text: message,
    fileNames: files.length ? files.map((file) => file.name) : undefined,
  });
  input.value = "";
  pendingFiles.value = [];
  const localReply = props.answerLocally?.({ message, files }) ?? null;
  if (localReply !== null) {
    messages.value.push({ role: "assistant", text: localReply });
    await scrollToBottom();
    return;
  }
  isSending.value = true;
  const epoch = chatEpoch;
  await scrollToBottom();

  try {
    const formData = new FormData();
    formData.append("company_id", String(props.companyId));
    // The reply matches the user's own words; a turn without any (an upload) is answered in the page's language.
    formData.append("language", locale.value);
    if (message) {
      formData.append("message", message);
    }
    for (const file of files) {
      formData.append("file", file);
    }
    if (conversationId.value !== null) {
      formData.append("conversation_id", String(conversationId.value));
    }
    for (const [key, value] of Object.entries(props.extraFields ?? {})) {
      formData.append(key, value);
    }

    const response = await goSolidApi.post<{ data?: ChatTurnResponse }>(
      props.endpoint,
      formData
    );
    if (epoch !== chatEpoch) {
      return;
    }
    const data = response.data?.data;
    if (!data) {
      throw new Error("Unexpected chat response");
    }

    // A rejected turn continues its conversation too, so a follow-up question ("what went wrong?")
    // reaches the assistant together with the rejection.
    conversationId.value = data.conversation_id ?? conversationId.value;
    if ("correction" in data) {
      const correction =
        props.resolveCorrection?.(data.correction) ??
        genericCorrection(data.correction);
      // An unrecognized correction reads as a generic failure, support code included.
      if (!correction) globalErrorHandler(data);
      messages.value.push({
        role: "assistant",
        text: correction ?? t("ai_chat.error_reply"),
      });
    } else {
      messages.value.push({ role: "assistant", text: data.reply });
      if (data.result) {
        emit("analysis-complete", data.result);
      }
    }
  } catch (error) {
    if (epoch !== chatEpoch) {
      return;
    }
    globalErrorHandler(error);
    messages.value.push({ role: "assistant", text: t("ai_chat.error_reply") });
  } finally {
    isSending.value = false;
    await scrollToBottom();
  }
};

const resetChat = () => {
  chatEpoch += 1;
  conversationId.value = null;
  input.value = "";
  pendingFiles.value = [];
  greet();
  emit("analysis-complete", null);
};

// A host page may need to abandon the conversation itself (its draft lives server-side and would
// otherwise be resumed by the next turn), not only clear what it derived from the last result.
defineExpose({ resetChat });
</script>

<style scoped lang="scss">
.chat-thread {
  overflow-y: auto;
  min-height: 10rem;
  max-height: 20rem;
  padding-right: 0.25rem;
}

.chat-bubble {
  max-width: 85%;
  border-radius: 0.5rem;

  .chat-text {
    white-space: pre-wrap;
    word-break: break-word;
  }
}

.chat-bubble-user {
  background-color: var(--phoenix-primary-bg-subtle);
  color: var(--phoenix-primary-text-emphasis);
  border-bottom-right-radius: 0.25rem;
}

.chat-bubble-assistant {
  background-color: var(--phoenix-secondary-bg, var(--phoenix-gray-100));
  color: var(--phoenix-body-color);
  border-bottom-left-radius: 0.25rem;
}

.chat-attachment {
  color: var(--phoenix-primary);
}

.chat-input {
  resize: none;
}

.drop-overlay {
  inset: 0.5rem;
  z-index: 2;
  border: 2px dashed var(--phoenix-primary);
  border-radius: 0.5rem;
  background-color: var(--phoenix-primary-bg-subtle);
  // Lets the drop land on the card underneath, so the overlay itself never adds a dragenter/dragleave pair.
  pointer-events: none;

  .drop-overlay-icon {
    color: var(--phoenix-primary);
  }

  .drop-overlay-title {
    color: var(--phoenix-primary-text-emphasis);
  }
}

.drop-overlay-busy {
  border-color: var(--phoenix-gray-400);
  background-color: var(--phoenix-body-highlight-bg);

  .drop-overlay-icon {
    color: var(--phoenix-tertiary-color);
  }
}
</style>
